/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { debounce } from 'lodash';
import { withTranslation } from 'react-i18next';
import {
    InternalGrid,
    Text,
    Box,
    Card,
    DaInput,
    SearchIcon,
    DaSelect,
    ListIcon,
    EarthIcon,
} from '@paygreen/paygreen-ui';
import dayjs from '../../utils/dayjsHelper';
import i18n from '../../internationalization/i18n';

import { IsMobile } from '../../utils/tools';
import { Country } from 'api-green-node';
import { useForm } from 'react-hook-form';
import { getSustainableGoals } from './service';
import { useAPIError } from '../../context';
import { frLangLocale } from '../../utils/constants';

const ProjectFilter = ({ t, projects, setIsLoading, fetchData }) => {
    const [country, setCountry] = useState([]);
    const [goals, setGoals] = useState([]);
    const [minCarbonPrice, setMin] = useState(null);
    const [maxCarbonPrice, setMax] = useState(null);

    const [rawOdd, setRawOdd] = useState([]);
    const [rawProjects, setRawProjects] = useState([]);
    const { register, setValue, getValues } = useForm();

    const userLanguage = dayjs.locale(frLangLocale.includes(i18n.language));

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const { addError } = useAPIError();

    const debounceFn = useCallback(debounce(fetchData, 500), []);

    const getCarbonPrice = (array) => {
        return array?.reduce((acc, item) => {
            acc.push(item.carbonPrice);

            return acc;
        }, []);
    };

    const getData = useCallback(() => {
        getSustainableGoals()
            .then((res) => {
                const data = res.data._embedded.sustainable_development_goal;

                const carbonPrice = getCarbonPrice(projects);

                if (carbonPrice) {
                    const max = Math.max(...carbonPrice);
                    const min = Math.min(...carbonPrice);

                    setMin(min);
                    setMax(max);
                }

                setRawOdd(data);

                getOdd();

                setIsLoading(false);
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
                setIsLoading(false);
            });
    }, [projects]);

    const getOdd = () => {
        const goalOptions = rawOdd?.reduce((acc, curr) => {
            if (userLanguage === 'fr') {
                const item = {
                    text: `${t('contributions.globalView.number')} - ${
                        curr.id
                    } ${curr.nameFrench}`,
                    value: curr.id,
                };

                acc.push(item);
                return acc;
            }
            const item = {
                text: `n°${curr.id} ${curr.nameEnglish}`,
                value: curr.id,
            };

            acc.push(item);
            return acc;
        }, []);

        let odds = [];

        const updatedOdd = projects?.reduce((acc, project) => {
            project.sustainableDevelopmentGoals.forEach((goal) => {
                const filteredOdd = goalOptions.find((option) => {
                    return option.value === goal.id;
                });

                if (filteredOdd) {
                    if (!odds.includes(filteredOdd.value)) {
                        odds.push(filteredOdd.value);
                        acc.push(filteredOdd);
                    }
                }
            });

            return acc;
        }, []);

        if (updatedOdd) {
            updatedOdd.unshift({
                text: t('contributions.globalView.selectDefaultOdd'),
                value: '',
            });

            updatedOdd.sort((a, b) => a.value - b.value);

            setGoals(updatedOdd);
        }
    };

    useEffect(() => {
        isMounted.current = true;

        if (isMounted.current && !minCarbonPrice) {
            getData();

            setRawProjects(projects);
        }

        getOdd();

        return () => {
            isMounted.current = false;
        };
    }, [rawOdd, userLanguage]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setValue(name, value);

        setIsLoading(true);

        debounceFn(getValues());
    };

    useEffect(() => {
        const getCountry = () => {
            const countryOptions = Object.keys(Country)
                .slice(0, Object.keys(Country).length / 2)
                .map((option) => {
                    return {
                        value: Country[option],
                        text: t(`countries:${Country[option]}`),
                    };
                });

            return rawProjects?.reduce((acc, project) => {
                const filteredCountry = countryOptions.find((option) => {
                    return option.value === project.country;
                });

                acc.push(filteredCountry);
                return acc;
            }, []);
        };

        if (rawProjects) {
            const filteredCountry = getCountry();

            if (filteredCountry) {
                filteredCountry.unshift({
                    text: t('contributions.globalView.selectDefaultCountry'),
                    value: '',
                });

                setCountry(filteredCountry);
            }
        }
    }, [projects, t, setValue, rawProjects]);

    const renderFilter = () => {
        return (
            <Card
                style={{ maxWidth: '1000px' }}
                colorWab="white10"
                shadowSize="none"
                paddingLateral="xs"
                paddingTop="xs"
                radiusSize="sm"
                blockWidth="xl"
            >
                <InternalGrid
                    style={{ width: '100%' }}
                    alignItems="center"
                    justifyContent="flex-start"
                    justifyItems="stretch"
                    displayType="grid"
                    hasStaticWidth={false}
                    gridTemplateColumns={IsMobile() ? '1fr' : '3fr 2fr 1fr'}
                    childrenMarginBottom={IsMobile() ? 'sm' : 'md'}
                >
                    <Box marginRight="sm">
                        <DaInput
                            name="name"
                            onChange={handleChange}
                            placeholder={t(
                                'contributions.globalView.searchInput',
                            )}
                            blockWidth="lg"
                            fieldSize="md"
                            inputRef={register}
                            icon={<SearchIcon />}
                        />
                    </Box>

                    <Box marginRight="sm">
                        <DaSelect
                            name="sdg"
                            onChange={handleChange}
                            blockWidth="lg"
                            fieldSize="md"
                            options={goals}
                            inputRef={register}
                            icon={<ListIcon />}
                        />
                    </Box>

                    <Box marginRight="sm">
                        <DaSelect
                            name="country"
                            onChange={handleChange}
                            options={country}
                            blockWidth="lg"
                            fieldSize="md"
                            inputRef={register}
                            icon={<EarthIcon />}
                        />
                    </Box>
                </InternalGrid>

                <InternalGrid
                    alignItems="center"
                    justifyContent="flex-start"
                    hasStaticWidth={false}
                    childrenMarginBottom={IsMobile() ? 'sm' : 'md'}
                >
                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="auto auto 100px"
                        gridGap="md"
                        alignItems="center"
                    >
                        <Text textSize="xs">
                            {t('contributions.globalView.tonneOf')}{' '}
                        </Text>

                        <Text textSize="xs">
                            {t('contributions.globalView.from')}
                        </Text>

                        <Box marginRight="sm">
                            <DaInput
                                type="number"
                                name="minPrice"
                                step="0.01"
                                min={
                                    minCarbonPrice &&
                                    (
                                        Number.parseFloat(minCarbonPrice) / 100
                                    ).toFixed(2)
                                }
                                blockWidth="xs"
                                defaultValue={
                                    minCarbonPrice &&
                                    (
                                        Number.parseFloat(minCarbonPrice) / 100
                                    ).toFixed(2)
                                }
                                inputRef={register}
                                onChange={handleChange}
                            />
                        </Box>
                    </InternalGrid>

                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="auto auto"
                        gridGap="md"
                        alignItems="center"
                    >
                        <Text textSize="xs">
                            {t('contributions.globalView.euro')}
                        </Text>
                    </InternalGrid>

                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="auto 100px"
                        gridGap="md"
                        alignItems="center"
                    >
                        <Text textSize="xs">
                            {t('contributions.globalView.to')}
                        </Text>

                        <Box marginRight="sm">
                            <DaInput
                                type="number"
                                name="maxPrice"
                                blockWidth="xs"
                                step="0.01"
                                max={
                                    maxCarbonPrice &&
                                    (
                                        Number.parseFloat(maxCarbonPrice) / 100
                                    ).toFixed(2)
                                }
                                defaultValue={
                                    maxCarbonPrice &&
                                    (
                                        Number.parseFloat(maxCarbonPrice) / 100
                                    ).toFixed(2)
                                }
                                inputRef={register}
                                onChange={handleChange}
                            />
                        </Box>
                    </InternalGrid>

                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="auto auto"
                        gridGap="md"
                        alignItems="center"
                    >
                        <Text textSize="xs">
                            {t('contributions.globalView.euro')}
                        </Text>
                    </InternalGrid>
                </InternalGrid>
            </Card>
        );
    };

    return <>{renderFilter()}</>;
};

export default withTranslation('common')(ProjectFilter);
